import React, { useEffect, useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { connect } from 'react-redux';

import messages from 'constants/messages';
// @ts-ignore
import { getProject } from 'store/services/project/selectors';
// @ts-ignore
import useVersionControl from 'hooks/useVersionControl';
import { ActionType } from 'hooks/useVersionControl/types';
import { refreshNLUState, RefreshNLUState } from '../../utils';
import LoadingModal from './modals/LoadingModal';
import CommitFormModal from './modals/CommitFormModal';
import ConflictsWarningModal from './modals/ConflictsWarningModal';
import FetchRequiredModal from './modals/FetchRequiredModal/FetchRequiredModal';
import ModalContainer from './components/ModalContainer';
import ErrorModal from './modals/ErrorModal';

export type OwnProps = {
  selectedAction: ActionType;
  onCancel: () => void;
};

type DispatchedProps = {
  refreshNLUState: RefreshNLUState;
  project: { account: string; slug: string };
};

type PullResponse = {
  conflicts?: unknown[];
};

export type CommitAndFetchModalProps = OwnProps & Partial<DispatchedProps>;

const CommitAndFetchModal = ({
  selectedAction,
  onCancel,
  refreshNLUState,
  project,
}: CommitAndFetchModalProps) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const {
    loading: loadingCommit,
    error: commitError,
    action: commitRequest,
  } = useVersionControl('commit');
  const {
    loading: loadingPull,
    error: pullError,
    action: pullRequest,
  } = useVersionControl('pull');
  const {
    loading: loadingGitStatus,
    error: gitStatusError,
    action: checkGitStatusRequest,
  } = useVersionControl('gitStatus');
  const [hasConflicts, setHasConflicts] = useState<boolean>(false);

  const handlePullSuccess = useCallback(
    pullResponse => {
      if (pullResponse && refreshNLUState) {
        refreshNLUState(pullResponse);
      }
      onCancel();
    },
    [onCancel, refreshNLUState],
  );

  const handleFetch = useCallback(async () => {
    const pullResponse = (await pullRequest(handlePullSuccess)) as PullResponse;

    if (pullResponse?.conflicts && pullResponse.conflicts.length > 0) {
      setHasConflicts(true);
    }
  }, [pullRequest, handlePullSuccess]);

  const handleResolveConflicts = () =>
    history.push(
      `/${project?.account}/${
        project?.slug
      }/resolve-conflicts?from=${encodeURIComponent(pathname)}`,
    );

  const handleCommit = (commitMessage: string) =>
    commitRequest(commitMessage, onCancel);

  useEffect(() => {
    if (selectedAction === 'commit') {
      checkGitStatusRequest(selectedAction);
    } else if (selectedAction === 'pull') {
      handleFetch();
    }
  }, [selectedAction, checkGitStatusRequest, handlePullSuccess, handleFetch]);

  const error = commitError || pullError || gitStatusError;
  const showFetchRequired =
    selectedAction === 'commit' && gitStatusError === 'Fetch Required!';
  const showLoading = loadingPull || loadingGitStatus;
  const showCommitError = selectedAction === 'commit' && Boolean(error);
  const showFetchError = Boolean(pullError);
  const errorTitle = showFetchError
    ? messages.VERSION_CONTROL.MODAL.FETCH.ERROR_TITLE
    : messages.VERSION_CONTROL.MODAL.COMMIT.ERROR_TITLE;

  const renderModal = () => {
    if (hasConflicts) {
      return (
        <ConflictsWarningModal
          onCancel={onCancel}
          onSubmit={handleResolveConflicts}
        />
      );
    }

    if (showFetchRequired) {
      return (
        <FetchRequiredModal
          loading={loadingPull}
          onCancel={onCancel}
          onSubmit={handleFetch}
        />
      );
    }

    if (showLoading) {
      return (
        <LoadingModal
          isFetch={loadingPull || selectedAction === 'pull'}
          loadingStatus={loadingGitStatus}
        />
      );
    }

    if (showCommitError || showFetchError) {
      return (
        <ErrorModal errorText={error} title={errorTitle} onCancel={onCancel} />
      );
    }

    return (
      <CommitFormModal
        loading={loadingCommit}
        onCancel={onCancel}
        onSubmit={handleCommit}
      />
    );
  };

  return <ModalContainer>{renderModal()}</ModalContainer>;
};

const mapStateToProps = (state: unknown, props: CommitAndFetchModalProps) => ({
  ...props,
  project: getProject(state),
});

export default connect(mapStateToProps, {
  refreshNLUState,
})(CommitAndFetchModal);
